import { useMemo } from "react";

import { useHistory } from "react-router-dom";
import { CommitmentManagersModel } from "@doitintl/cmp-models";
import { getCollection, type ModelIdRef } from "@doitintl/models-any";
import { useCollectionData } from "@doitintl/models-hooks";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { Loader } from "../../Components/Loader";
import { useCustomerContext } from "../../Context/CustomerContext";
import { useUserContext } from "../../Context/UserContext";
import useSegmentTrackEvent from "../../utils/useSegmentTrackEvent";
import { CommitmentCard } from "./CommitmentCard";
import { Onboarding } from "./Onboarding";

export const CommitmentManagerList = () => {
  const { customerOrPresentationModeCustomer, customer } = useCustomerContext();
  const { userRoles } = useUserContext();
  const [commitmentManagers, loading] = useCollectionData(
    getCollection(CommitmentManagersModel).where("customerId", "==", customerOrPresentationModeCustomer.id),
    {
      idField: "id",
      refField: "ref",
    }
  );
  const history = useHistory();
  const { trackEvent } = useSegmentTrackEvent();

  const { activeCommitments, inactiveCommitments } = useMemo(() => {
    if (!commitmentManagers) {
      return { activeCommitments: [], inactiveCommitments: [] };
    }

    const now = DateTime.now().toUTC();
    const active: ModelIdRef<CommitmentManagersModel>[] = [];
    const inactive: ModelIdRef<CommitmentManagersModel>[] = [];

    commitmentManagers.forEach((commitment) => {
      if (!commitment.periods || commitment.periods.length === 0) {
        return;
      }

      const lastPeriod = commitment.periods[commitment.periods.length - 1];
      const endDate = DateTime.fromJSDate(lastPeriod.endDate.toDate()).toUTC();

      if (endDate < now) {
        inactive.push(commitment);
      } else {
        active.push(commitment);
      }
    });

    return { activeCommitments: active, inactiveCommitments: inactive };
  }, [commitmentManagers]);

  const handleCreateCommitment = () => {
    trackEvent("Create Commitment Selected", {
      feature: "Commitment Manager",
      source: "Commitment Manager",
    });
    history.push(`/customers/${customer.id}/monitor/commitment-manager/create`);
  };

  const handleCardClick = (commitmentId: string) => {
    history.push(`/customers/${customer.id}/monitor/commitment-manager/${commitmentId}`);
  };

  return (
    <Loader loading={loading}>
      {commitmentManagers && commitmentManagers.length > 0 ? (
        <Stack spacing={2} sx={{ width: { lg: "1000px" }, mx: "auto", my: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h1">Commitment Manager</Typography>
              <Typography variant="body1" color="text.secondary">
                Track and manage your cloud commitment spend
              </Typography>
            </Box>
            <Tooltip
              title={
                !userRoles?.cloudAnalyticsAdmin && !userRoles?.doitEmployee
                  ? "You need Cloud Analytics Admin permission to create commitments"
                  : ""
              }
              arrow
              disableHoverListener={!!userRoles?.cloudAnalyticsAdmin && !!userRoles?.doitEmployee}
              placement="bottom"
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateCommitment}
                  disabled={!userRoles?.cloudAnalyticsAdmin && !userRoles?.doitEmployee}
                >
                  Create commitment
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <Box>
            {activeCommitments.map((commitment) => (
              <CommitmentCard key={commitment.id} commitment={commitment} onCardClick={handleCardClick} />
            ))}
          </Box>

          {inactiveCommitments.length > 0 && (
            <Stack spacing={2}>
              <Typography variant="h4">Inactive commitments</Typography>
              {inactiveCommitments.map((commitment) => (
                <CommitmentCard key={commitment.id} commitment={commitment} onCardClick={handleCardClick} />
              ))}
            </Stack>
          )}
        </Stack>
      ) : (
        <Box sx={{ mt: 6 }}>
          <Onboarding />
        </Box>
      )}
    </Loader>
  );
};
